/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Fragment, ReactNode, useCallback, useContext } from "react";
import "reset-css";
import { boolean } from "superstruct";
import Icon from "../../../components/Icon";
import Panel3 from "../../../components/Panel3";
import Spacer from "../../../components/Spacer";
import Text from "../../../components/Text";
import {
  CheckboxDefaultField,
  useFieldRequiredContraint,
  useMountedField,
} from "../../../utilities/Fields";
import Layout from "../../../utilities/Layout";
import StepContext from "../../../utilities/StepContext";
import Theme from "../../../utilities/Theme";
import useResponsive from "../../../utilities/useResponsive";
import { FieldsFormContext } from "./FieldsForm";

export type CheckboxDefaultFieldHandlerProps = {
  field: CheckboxDefaultField;
};

export default function CheckboxDefaultFieldHandler(
  props: CheckboxDefaultFieldHandlerProps
) {
  const { field } = props;

  const form = useContext(FieldsFormContext);
  const { value, setValue } = form.useFieldManager(field.id, boolean());
  useFieldRequiredContraint(field);
  useMountedField(field);

  const theme = Theme.useTheme();

  const select = useCallback(() => {
    setValue(true);
  }, [field]);

  const unselect = useCallback(() => {
    setValue(false);
  }, [field]);

  StepContext.useResumeUpdater(field, value ? "Oui" : "Non");

  const containerCss = css`
    padding-top: ${Layout.S / 2}px;
    padding-bottom: ${Layout.S / 2}px;
    padding-left: ${Layout.S}px;
    padding-right: ${Layout.S}px;
  `;

  let imageNode: ReactNode = null;

  const sizingProp = useResponsive({
    800: "height",
    bigger: "width",
  });

  if (field.lightbox) {
    const imageCss = css`
      margin: 4px;
      background-image: url(${field.lightbox.src});
      background-size: cover;
      background-position: center;
      border-radius: ${Layout.R}px;
      flex-shrink: 0;
      ${sizingProp}: 200px;
    `;
    imageNode = <div css={imageCss}></div>;
  }

  const horizontal = useResponsive({
    800: false,
    bigger: true,
  });

  const lineCss = css({
    display: "flex",
    alignItems: "center",
  });

  return (
    <div css={containerCss}>
      <Panel3
        horizontal={horizontal}
        onClick={value ? unselect : select}
        content={
          <Fragment>
            <Spacer.Half />
            <Text typo="subheading">{field.title}</Text>
            <Spacer />
            <div css={lineCss}>
              <Icon
                name={value ? "checkbox-marked" : "checkbox-blank-outline"}
                size={24}
                color={value ? theme.validationBackground : "black"}
              />
              <Text html={field.description}></Text>
            </div>
            <Spacer.Half />
            <Spacer />
          </Fragment>
        }
      />
    </div>
  );
}
